import React, { useCallback, useEffect, useState } from "react";
import { Chip, Paper, Skeleton, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell, pageTypography } from "../components/PageLayout";
import VacationForm from "../components/VacationForm";
import { useAuth } from "../contexts/AuthContext";
import { useUI } from "../contexts/UIContext";

const statusColor = {
  APPROVATA: "success",
  IN_ATTESA: "warning",
  RIFIUTATA: "error",
};

export default function Ferie() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const { showNotification } = useUI();
  const { hasAnyRole } = useAuth();
  const canApprove = hasAnyRole(["SUPERADMIN", "ADMIN", "MANAGER"]);

  const normalizeFerie = (item) => ({
    id: item?.id,
    employeeName: item?.dipendente?.nome || "-",
    type: item?.tipo || "FERIE",
    startDate: item?.dataInizio || "",
    endDate: item?.dataFine || "",
    status: item?.stato || "IN_ATTESA",
  });

  const loadVacations = useCallback(async () => {
    setLoading(true);
    try {
      const [vacationsResponse, employeesResponse] = await Promise.all([
        httpClient.get("/ferie"),
        httpClient.get("/dipendenti"),
      ]);
      setRows((vacationsResponse?.data || []).map(normalizeFerie));
      setEmployees(employeesResponse?.data || []);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadVacations();
  }, [loadVacations]);

  const submitVacation = async (payload) => {
    try {
      await httpClient.post("/ferie", {
        dipendenteId: Number(payload.dipendenteId),
        dataInizio: payload.startDate,
        dataFine: payload.endDate,
        tipo: payload.type,
      });
      showNotification("Richiesta ferie inviata", "success");
      await loadVacations();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const updateVacationStatus = async (id, action) => {
    try {
      await httpClient.put(`/ferie/${id}/${action}`);
      showNotification(
        action === "approva" ? "Ferie approvate" : "Ferie rifiutate",
        "success"
      );
      await loadVacations();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "employeeName", headerName: "Dipendente", minWidth: 180, flex: 1 },
    { field: "type", headerName: "Tipo", minWidth: 120 },
    { field: "startDate", headerName: "Dal", minWidth: 120 },
    { field: "endDate", headerName: "Al", minWidth: 120 },
    {
      field: "status",
      headerName: "Stato",
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.value || "IN_ATTESA"}
          color={statusColor[params.value] || "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      width: 110,
      getActions: (params) => {
        if (!canApprove || params.row.status !== "IN_ATTESA") {
          return [];
        }

        return [
          <GridActionsCellItem
            icon={<CheckCircleOutlineIcon />}
            label="Approva"
            onClick={() => updateVacationStatus(params.id, "approva")}
          />,
          <GridActionsCellItem
            icon={<HighlightOffIcon />}
            label="Rifiuta"
            onClick={() => updateVacationStatus(params.id, "rifiuta")}
          />,
        ];
      },
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Gestione ferie"
        subtitle="Richieste, approvazioni e storico ferie con superfici e accenti coerenti con la tabella turni."
      />
      <VacationForm onSubmit={submitVacation} employees={employees} />
      <PagePanel tone="neutral" sx={{ p: 1.5 }}>
        <Typography sx={{ ...pageTypography.panelTitle, mb: 1.5 }}>
          Lista richieste
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" height={360} />
        ) : (
          <DataGrid rows={rows} columns={columns} autoHeight disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} sx={dataGridSx} />
        )}
      </PagePanel>
    </PageShell>
  );
}
